import { Box, Button, Tab, Tabs } from '@mui/material';
import { useState, FC, useEffect, useRef } from 'react';
import styles from 'pages/insured-person-case/InsuredPersonCase.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { fetchInsuredPersonCase, fetchPolicyDownload } from 'entities/insured-person-case/actions/fetch-insured-person-case';
import { Download, ErrorDownload, FileData, FileName, InsuredPerson } from 'entities/insured-person-case/selector';
import { CustomTabPanel } from 'shared/custom-tab-panel/custom-tab-panel';
import PersonInfo from 'features/insured-person-case/ui/PersonInfo';
import ProgramCase from 'pages/program-case/ProgramCase';
import appStyles from 'app/providers/theme/App.module.css';
import CustomButton from 'shared/custom-button/CustomButton';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { resetFileData } from 'entities/insured-person-case';
import { AlertMessage } from 'shared/alert-message/alert-message';
import Loader from 'shared/loader/Loader';
import GuaranteeLetter from 'features/insured-person-case/ui/GuaranteeLetter';

interface IAlertInfo {
    message?: string;
    severity?: string;
}

export const InsuredPersonCase: FC = () => {
    const dispatch = useAppDispatch();
    const useQuery = () => new URLSearchParams(useLocation().search);
    const query = useQuery();
    const policyID = query.get('id');
    const caseData = useAppSelector(InsuredPerson);
    const navigate = useNavigate();
    const location = useLocation();

    const handleClickBack = () => {
        if (location.state?.fromInsuranceContractPage) {
            navigate(`/insurance-contracts/case/?id=${location.state.id}`, {
                state: {
                    textRout: 'insurance-contracts',
                    name: 'Договоры страхования',
                    activeTab: location.state.activeTab
                }
            });
        } else {
            navigate('/insured-persons', {
                state: {
                    textRout: 'list-of-insured-persons',
                    text: 'Список застрахованных'
                }
            });
        }
    };

    useEffect(() => {
        if (policyID) {
            dispatch(fetchInsuredPersonCase({ PolicyID: policyID }));
        }
    }, [policyID]);

    const [value, setValue] = useState(0);
    const a11yProps = (index: number) => {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    };
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };


    const tabsRef = useRef<HTMLDivElement>(null);
    const handleWheel = (event: React.WheelEvent) => {
        if (tabsRef.current) {
            const tabsContainer = tabsRef.current.querySelector('.MuiTabs-scroller');
            if (tabsContainer) {
                tabsContainer.scrollLeft += event.deltaY * 4;
            }
        }
    };

    const [alertInfo, setAlertInfo] = useState<IAlertInfo>({ message: '', severity: '' });

    const [downloadTriggered, setDownloadTriggered] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const fileData = useAppSelector(FileData);
    const fileName = useAppSelector(FileName);
    const isDownload = useAppSelector(Download);
    const errorDownload = useAppSelector(ErrorDownload);

    const handleFileDownload = async (PolicyID: string, DataType: string) => {
        setAlertInfo({ message: '', severity: '' });

        try {
            setIsDownloading(true);

            await dispatch(fetchPolicyDownload({
                PolicyID: PolicyID,
                DataType: DataType
            }));

            setDownloadTriggered(true);
        } catch (error) {
            console.error('Error downloading the file:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        if (downloadTriggered && fileData && errorDownload.length === 0) {
            const link = document.createElement('a');
            link.href = `data:application/octet-stream;base64,${fileData}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            dispatch(resetFileData());
            setDownloadTriggered(false);
        } else if (errorDownload.length > 0) {
            if (errorDownload[0].Msg.startsWith('Не удалось получить файл по пути')) {
                setAlertInfo({ message: 'Не удалось найти полис. Обратитесь, пожалуйста, в поддержку.', severity: 'error' });
            } else {
                setAlertInfo({ message: errorDownload[0].Msg, severity: 'error' });
            }

            const timeoutId = setTimeout(() => {
                setAlertInfo({ message: '', severity: '' });
                dispatch(resetFileData());
            }, 3000);
            return () => clearTimeout(timeoutId);
        }
    }, [downloadTriggered, fileData, errorDownload, fileName, dispatch]);


    const handleDownload = async () => {
        if (policyID) {
            await handleFileDownload(policyID, 'Полис(Полис)');
        }
    }

    return (
        <>
            {alertInfo.message && (
                <Box className={appStyles.alertBox}>
                    <AlertMessage alertInfo={alertInfo} />
                </Box>
            )}
            <Box className={appStyles.titleBox}>
                <h1>{caseData.FullName ? caseData.FullName : ''}</h1>
                <Box className={appStyles.titleBtn}>
                    <Button onClick={handleClickBack} className={appStyles.btnBack}><KeyboardBackspaceIcon /> Назад</Button>
                </Box>
            </Box>
            {isDownload ? (
                <Loader
                    title='Файл скачивается... Пожалуйста, подождите'
                />
            ) : (
                <>
                    <Box className={styles.flexBox}>
                        <p className={styles.status}>
                            Статус:
                            <span className={caseData.Active ? styles.green : styles.red}>
                                <span className={`${styles.statusIndicator} ${caseData.Active ? styles.greenIndicator : styles.redIndicator}`}></span>
                                {caseData.Active ? 'Прикреплен' : 'Откреплен'}
                            </span>
                        </p>
                        {caseData.HasFile && (
                            <Box>
                                <CustomButton
                                    icon={FileDownloadIcon}
                                    rightIcon={true}
                                    handleClick={handleDownload}
                                    title="Скачать полис"
                                    textColor='#0054E6'
                                    backgroundColor="none"
                                    border='none'
                                    borderRadius='4px'
                                    padding="8px 30px"
                                />
                            </Box>
                        )}

                    </Box>
                    <Box onWheel={handleWheel} ref={tabsRef} className={styles.tabsBox}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="scrollable auto tabs example"
                        >
                            <Tab label="Персональные данные" {...a11yProps(0)} />
                            <Tab label="Программа страхования" {...a11yProps(1)} />
                            {caseData.ShowGuaranteeLetters && (
                                <Tab label="Гарантийные письма" {...a11yProps(2)} />
                            )}

                        </Tabs>
                    </Box>
                    <CustomTabPanel value={value} index={0}>
                        <PersonInfo />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={1}>
                        <ProgramCase
                            isContract={true}
                            contrsctProgramID={caseData.ProgramID}
                        />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={2}>
                        <GuaranteeLetter
                            contrsctProgramID={policyID || ''}
                        />
                    </CustomTabPanel>
                </>
            )}


        </>
    );
};

export default InsuredPersonCase;
